import type { ReactNode } from 'react';
import { useMapStore } from './MapContext';
import MapView from './MapView';
import LayerPanel from './components/LayerPanel';
import ParcelInspector from './components/ParcelInspector';
import InfrastructureInspector from './components/InfrastructureInspector';
import FeatureInspector from './components/FeatureInspector';
import DrawToolbar from './components/DrawToolbar';
import FileImportOverlay from './components/FileImportOverlay';
import ContextMenu from './components/ContextMenu';
import DevicePalette from './components/DevicePalette';
import ReviewTray from './components/ReviewTray';
import LiveObjectsLayer from './components/LiveObjectsLayer';
import LiveTimelineStrip from './components/LiveTimelineStrip';
import MapperImportDrawer from './components/MapperImportDrawer';
import FindByEntityDrawer from './components/FindByEntityDrawer';
import type { MapperEmbedPreset } from './mapperPresets';

interface MapperEmbedProps {
  preset?: MapperEmbedPreset;
  visibleObjectTypes?: string[];
  quickPresetIds?: string[];
  editingEnabled?: boolean;
  showSidePanel?: boolean;
  /**
   * Controls the in-map mode toolbar. When the header hosts the toolbar
   * (header layout), pass false to avoid a duplicate floating toolbar.
   */
  showToolbar?: boolean;
  showImportOverlay?: boolean;
  showContextMenu?: boolean;
  showDevicePalette?: boolean;
  /** Render the docked time/live strip at the base of the map (default true). */
  showLiveStrip?: boolean;
  reviewSlot?: ReactNode;
  overlaySlot?: ReactNode;
}

export default function MapperEmbed({
  preset,
  visibleObjectTypes,
  quickPresetIds,
  editingEnabled,
  showSidePanel = true,
  showToolbar,
  showImportOverlay = true,
  showContextMenu = true,
  showDevicePalette = true,
  showLiveStrip = true,
  reviewSlot = null,
  overlaySlot = null,
}: MapperEmbedProps) {
  const {
    selectedInfrastructure,
    activeParcel,
    parcelInspectorOpen,
    livePositions,
  } = useMapStore();

  const effectiveEditingEnabled = editingEnabled ?? preset?.editingEnabled ?? true;
  const effectiveVisibleObjectTypes = visibleObjectTypes ?? preset?.visibleObjectTypes;
  const effectiveQuickPresetIds = quickPresetIds ?? preset?.quickPresetIds;
  const shouldShowToolbar = showToolbar ?? effectiveEditingEnabled;

  return (
    <div className="relative flex min-h-0 flex-1 bg-white">
      {showSidePanel && (
        <div className="relative z-20 flex h-full w-72 flex-shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-white">
          <LayerPanel />
          {parcelInspectorOpen && activeParcel ? (
            <ParcelInspector />
          ) : selectedInfrastructure ? (
            <InfrastructureInspector />
          ) : (
            <FeatureInspector />
          )}
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="relative min-h-0 flex-1">
          <MapView visibleObjectTypes={effectiveVisibleObjectTypes} />
          {shouldShowToolbar && <DrawToolbar quickPresetIds={effectiveQuickPresetIds} />}
          {showImportOverlay && <FileImportOverlay />}
          {showContextMenu && <ContextMenu />}
          {showDevicePalette && <DevicePalette />}
          <LiveObjectsLayer linkedDeviceIds={livePositions.map((p) => p.deviceId)} />
          <ReviewTray parcelId={activeParcel?.id} />
          {showImportOverlay && <MapperImportDrawer />}
          <FindByEntityDrawer />
          {reviewSlot}
          {overlaySlot}
        </div>
        {showLiveStrip && <LiveTimelineStrip />}
      </div>
    </div>
  );
}
